import React, { useEffect, useState, useMemo } from 'react';
import { CardContent, CircularProgress, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// Util import
import {
    getTotalValue,
    getListByFilter,
    applyOrdersFilter,
    applySearchFilter,
    applyBrandFilter,
    getCartsListByFilter,
} from '~/util/orderPanelFunctions';


// Local components import
import PanelTitle from './PanelTitle';
import SearchBar from './SearchBar';
import FilterButtons from './FilterButtons';
import PanelTotals from './PanelTotals';
import PanelList from './PanelList';

import * as Style from './styles';

function OrderListPanel({
    orders,
    brandFilter,
    setBrandFilter,
    multimarca,
    ordersLoaded,
    setCurrentPage,
    cartsLoaded,
}) {
    // Hooks
    const { isFaturado } = useSelector(state => state.status);
    const location = useLocation().pathname;

    // Local states
    const [filter, setFilter] = useState('Todos');
    const [ordersFilter, setOrdersFilter] = useState('allOrders');
    const [searchFilter, setSearchFilter] = useState('');

    const isAtMaisCarrinho = location === '/maisCarrinho';

    const filteredOrders = useMemo(() => {
        if (!orders) return [];

        if (isAtMaisCarrinho) {
            return getCartsListByFilter(orders, filter);
        }

        let list = orders;
        list = getListByFilter(list, filter);

        if (ordersFilter && ordersFilter !== 'allOrders') {
            list = applyOrdersFilter(list, ordersFilter);
        }

        if (searchFilter) {
            list = applySearchFilter(list, searchFilter);
        }

        list = applyBrandFilter(list, brandFilter);

        return list;
    }, [
        orders,
        filter,
        ordersFilter,
        searchFilter,
        brandFilter,
        isAtMaisCarrinho,
    ]);

    const totalFilterValue = useMemo(() => {
        if (isAtMaisCarrinho) return 'R$ 0,00';
        return getTotalValue(filteredOrders, ordersFilter);
    }, [filteredOrders, ordersFilter, isAtMaisCarrinho]);

    const hideOrdersFilter = useMemo(() => {
        return !['Todos', 'Vendas'].includes(filter);
    }, [filter]);

    const totalPanelCondition =
        [].includes(filter) &&
        ['allBrands', 'otherBrands', 'myBrands'].includes(brandFilter);

    useEffect(() => {
        if (isFaturado) {
            setFilter('Todos');
        }
    }, [isFaturado]);


    return (
        <>
            {ordersLoaded ? (
                <>
                    <Style.ComponentRoot>
                        <PanelTitle
                            ordersFilter={ordersFilter}
                            setOrdersFilter={setOrdersFilter}
                            brandFilter={brandFilter}
                            setBrandFilter={setBrandFilter}
                            hideOrdersFilter={hideOrdersFilter}
                            orders={orders}
                            filter={filter}
                            searchFilter={searchFilter}
                        />
                        {!isAtMaisCarrinho && (
                            <SearchBar
                                orders={orders}
                                filter={filter}
                                ordersFilter={ordersFilter}
                                searchFilter={searchFilter}
                                setSearchFilter={setSearchFilter}
                                brandFilter={brandFilter}
                            />
                        )}
                        <FilterButtons
                            orders={orders}
                            filter={filter}
                            setFilter={setFilter}
                            ordersFilter={ordersFilter}
                            setOrdersFilter={setOrdersFilter}
                            searchFilter={searchFilter}
                            brandFilter={brandFilter}
                        />

                        {!totalPanelCondition && !isAtMaisCarrinho && (
                            <PanelTotals
                                ordersFilter={ordersFilter}
                                filter={filter}
                                brandFilter={brandFilter}
                                totalFilterValue={totalFilterValue}
                            />
                        )}
                        <CardContent>
                            {!filteredOrders?.length ? (
                                <Style.ListContainer>
                                    <Style.ListItemContainer>
                                        {isAtMaisCarrinho ? (
                                            <>
                                                {!cartsLoaded ? (
                                                    <Grid
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'center',
                                                            marginTop: '1rem',
                                                        }}
                                                    >
                                                        <CircularProgress />
                                                    </Grid>
                                                ) : (
                                                    'Sem carrinhos'
                                                )}
                                            </>
                                        ) : (
                                            'Sem pedidos'
                                        )}
                                    </Style.ListItemContainer>
                                </Style.ListContainer>
                            ) : (
                                <>
                                    <PanelList
                                        filteredOrders={filteredOrders}
                                        multimarca={multimarca}
                                        setCurrentPage={setCurrentPage}
                                        cartsLoaded={cartsLoaded}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Style.ComponentRoot>
                </>
            ) : (
                <Grid
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '1rem',
                    }}
                >
                    <CircularProgress />
                </Grid>
            )}
        </>
    );
}

export default OrderListPanel;
